import { List, Empty, Button, Popconfirm, message } from 'antd';
import { EyebrowLabel } from '@viq-admin/ui';
import { useRoleClaim } from '../../auth/useRoleClaim';
import { unassignRep } from '../../api/discovery';

interface ConferenceEntry {
  id: string;
  name: string;
}

interface Props {
  oid: string;
  userCompanyId: string;
  conferences: ConferenceEntry[];
  onChanged: () => void;
}

/**
 * Displays the user's assigned conferences, with a per-row Remove
 * action for callers who are allowed to manage this user.
 */
export function ConferencesPanel({ oid, userCompanyId, conferences, onChanged }: Props) {
  const { role, companyId: callerCompanyId } = useRoleClaim();
  const canManage =
    role === 'company_admin' && callerCompanyId === userCompanyId;

  async function remove(confId: string, confName: string) {
    try {
      await unassignRep(confId, oid);
      message.success(`Removed ${confName}.`);
      onChanged();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Remove failed');
    }
  }

  return (
    <div>
      <EyebrowLabel>Conferences</EyebrowLabel>
      {conferences.length === 0 ? (
        <Empty description="No conferences assigned" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          size="small"
          dataSource={conferences}
          renderItem={(conf) => (
            <List.Item
              key={conf.id}
              actions={
                canManage
                  ? [
                      <Popconfirm
                        key="remove"
                        title={`Remove ${conf.name} from this user?`}
                        onConfirm={() => remove(conf.id, conf.name)}
                      >
                        <Button danger size="small" type="link">
                          Remove
                        </Button>
                      </Popconfirm>,
                    ]
                  : undefined
              }
            >
              {conf.name}
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
